import { InstancedMesh, Group, Matrix4, Object3D, Box3 } from 'three';

import Visualization from './Visualization';

import { createPointerHitListener } from './pointerEvents';
import { CustomEventTypes, PI_HALF, QUARTER_THRESHOLD } from './Constants';
import { CombinedTransformControls } from './CombinedTransformControls';

const _tempMatrix = new Matrix4();

function flipWindingOrder(geometry) {
  const index = geometry.index.array;
  for (let i = 0, il = index.length / 3; i < il; i++) {
    const x = index[i * 3];
    index[i * 3] = index[i * 3 + 2];
    index[i * 3 + 2] = x;
  }

  geometry.index.needsUpdate = true;
  return geometry;
}

function isMachinePart(object) {
  while (object) {
    if (object.userData.path) {
      return true;
    }
    object = object.parent;
  }
  return false;
}

function createInstanceContainer(sceneContainer, count) {
  sceneContainer.updateWorldMatrix(true, true);
  const geometries = {};
  sceneContainer.children
    .filter((model) => model.visible)
    .forEach((model) =>
      model.traverse((obj) => {
        if (obj.geometry) {
          const isFlipped = obj.matrixWorld.determinant() < 0;
          let geometry = obj.geometry;
          let geometryId = geometry.uuid;
          if (isFlipped) {
            geometryId += '-flipped';
          }
          if (!geometries[geometryId]) {
            geometries[geometryId] = {
              geometry: isFlipped ? flipWindingOrder(geometry.clone()) : geometry,
              material: obj.material,
              userData: model.userData,
              instances: [],
            };
          }
          geometries[geometryId].instances.push({
            matrix: obj.matrixWorld,
          });
        }
      })
    );
  const group = new Group();
  for (const { geometry, material, instances, userData } of Object.values(geometries)) {
    const baseCount = instances.length;
    const mesh = new InstancedMesh(geometry, material, baseCount * count);
    mesh.instances = instances;
    mesh.userData = userData;
    mesh.baseCount = baseCount;
    for (let c = 0; c < count; c++) {
      for (let i = 0; i < baseCount; i++) {
        mesh.setMatrixAt(baseCount * c + i, instances[i].matrix);
      }
    }
    group.add(mesh);
    geometry.computeBoundingSphere();
  }
  return group;
}

function disposeInstanceContainer(instanceContainer) {
  instanceContainer.children.forEach((obj) => {
    obj.removeFromParent();
    obj.dispose && obj.dispose();
  });
}

export default class VisualizationWithInstances extends Visualization {
  instances = [];

  setupDefaultControls() {
    super.setupDefaultControls();
    createPointerHitListener(this.renderer.domElement, (evt) => {
      if (!this.instancesContainer) {
        return;
      }

      const intersect = this.getIntersection(evt);

      let index = null;
      if (intersect?.target?.object && isMachinePart(intersect.target.object)) {
        index = intersect?.target?.instanceId;
        index = Number.isInteger(index) ? Math.floor(index / intersect.target.object.baseCount) : null;
      }

      this.setSelectedInstanceIndex(index);
    });
  }

  setSelectedInstanceIndex(index) {
    if (index === this.selectedInstanceIndex) {
      return; // no change
    }
    this.selectedInstanceIndex = index;
    this.observable.dispatch(CustomEventTypes.SelectedInstanceChanged, {
      selectedInstanceId: this.selectedInstanceIndex,
    });

    if (index === null) {
      if (this.transformControls?.object) {
        this.transformControls.detach();
        this.scene.remove(this.transformControls);
        this.render();
      }
      return;
    }
    this.initTransformControls();
  }

  getSelectedInstanceValues() {
    return Number.isInteger(this.selectedInstanceIndex) ? this.instances[this.selectedInstanceIndex] : null;
  }

  initTransformControls() {
    if (!this.transformControls) {
      const control = new CombinedTransformControls(this);

      const target = new Object3D();
      target.position.y = 1.5;
      target.rotation.reorder('YXZ');
      control.addEventListener('change', () => {
        if (control.activeControl?.object) {
          const mode = control.activeControl.getMode();
          const instanceValues = this.getSelectedInstanceValues();
          if (instanceValues) {
            if (mode === 'rotate') {
              const closestQuarter = Math.round(target.rotation.y / PI_HALF);
              const restToNextQuarter = target.rotation.y - closestQuarter * PI_HALF;
              if (Math.abs(restToNextQuarter) < QUARTER_THRESHOLD) {
                target.rotation.y -= restToNextQuarter;
              }
            }
            const { x, z } = target.position;
            const rotation = target.rotation.y;
            if (x !== instanceValues.x || z !== instanceValues.z || rotation !== instanceValues.rotation) {
              Object.assign(instanceValues, { x, z, rotation });
              this.updateInstancedMesh(instanceValues);
            }
          }
        }
        this.render();
      });

      control.addEventListener('dragging-changed', (event) => {
        this.controls.enabled = !event.value;
      });

      this.scene.add(target);
      this.scene.add(control);

      this.transformControlsTarget = target;
      this.transformControls = control;
    }

    const target = this.transformControlsTarget;
    const { x, z, rotation } = this.getSelectedInstanceValues();
    target.position.x = x;
    target.position.z = z;
    target.rotation.y = rotation;
    this.scene.add(this.transformControls);
    this.transformControls.attach(target);

    this.render();
  }

  getSceneContainerBox() {
    const box = new Box3();
    const container = this.instancesContainer || this.sceneContainer;
    container.updateMatrixWorld();
    container.updateWorldMatrix(true, true);
    container.traverse((object) => {
      if (object.visible && object.isMesh && object.name !== 'ground') {
        box.expandByObject(object);
      }
    });
    return box;
  }

  async updateDynamicParts(configuration) {
    super.updateDynamicParts(configuration);
    this.surroundingsOn && this.updateInstances(configuration);
  }

  updateInstances(configuration) {
    this.instances = configuration.customdata.instances;
    this.addInstancesToScene();
  }

  removeInstancesFromScene() {
    this.disposeCurrentInstances();
    if (this.scene && this.sceneContainer) {
      this.scene.add(this.sceneContainer);
      this.fitSceneToCamera();
      this.render();
    }
  }

  disposeCurrentInstances() {
    this.setSelectedInstanceIndex(null);
    if (!this.instancesContainer) {
      return;
    }
    this.scene.remove(this.instancesContainer);
    disposeInstanceContainer(this.instancesContainer);
    this.instancesContainer = null;
  }

  addInstancesToScene() {
    this.disposeCurrentInstances();
    const count = this.instances?.length;
    if (!count) {
      return;
    }
    console.time('addInstancesToScene');
    console.time('createInstanceContainer');
    this.instancesContainer = createInstanceContainer(this.sceneContainer, count);
    for (let i = 0; i < count; i++) {
      this.updateInstancedMesh(this.instances[i], i);
    }
    console.timeEnd('createInstanceContainer');

    console.time('render');
    this.scene.remove(this.sceneContainer).add(this.instancesContainer);
    this.fitSceneToCamera();

    this.render();
    console.timeEnd('render');
    console.timeEnd('addInstancesToScene');
  }

  updateInstancedMesh(instanceValues, index = this.selectedInstanceIndex) {
    const { x, z, rotation } = instanceValues;
    const matrix = _tempMatrix.makeRotationY(rotation).setPosition(x, 0, z);
    this.instancesContainer.children.forEach((obj) => {
      const { instances } = obj;
      const instanceCount = instances.length;
      for (let i = 0; i < instanceCount; i++) {
        const instanceMatrix = instances[i].matrix;
        obj.setMatrixAt(instanceCount * index + i, instanceMatrix.clone().premultiply(matrix));
      }
      obj.instanceMatrix.needsUpdate = true;
      obj.computeBoundingSphere();
    });
  }

  toggleSurroundings(on) {
    const result = super.toggleSurroundings(on);
    if (this.surroundingsOn) {
      this.addInstancesToScene();
    } else {
      this.removeInstancesFromScene();
    }
    return result;
  }

  setupScreenshots() {
    const restoreSuper = super.setupScreenshots();
    if (this.instancesContainer) {
      this.scene.remove(this.instancesContainer).add(this.sceneContainer);
    }
    return () => {
      restoreSuper();
      if (this.instancesContainer) {
        this.scene.remove(this.sceneContainer).add(this.instancesContainer);
      }
    };
  }
}
